// Attachments support
//    bucket - is the bucket to find the attachments
//    hideLoading - hide the loading indicator

import { StorageManager } from '@aws-amplify/ui-react-storage';
import { useState, useEffect, useCallback, useRef } from 'react';
import { DataCard, useStatusMessage } from '.';
import { AttachmentImage } from './AttachmentImage';
import { CardHeader } from './CardHeader';
import { TrashButton } from './TrashButton';
import { storageGet, storageList, storageDelete, addNameToStorage, processStorageList } from '../utilities/Database.mjs';
import { itemThumbnailPrefix } from '../fleet-shared/Urls';
import '@aws-amplify/ui-react/styles.css';

// The rule now lives in fleet-shared, because the preview Lambda needs it too
// and cannot import this file (StorageManager and an Amplify stylesheet come
// with it). Re-exported because this is where every caller already looks.
import {
  isImage,
  splitPreviews,
  previewFor,
  isGuestPhotoName,
  displayPhotoName,
  MAX_UPLOAD_FILE_SIZE,
} from '../fleet-shared/Attachments.mjs';
export { isImage };

function seperatePictureFromFiles(files) {
  var pictures = [];
  var data = [];

  for (var i=0; i < files.length; i++) {
    if (isImage(files[i].key)) {
      pictures.push(files[i]);
    } else {
      data.push(files[i]);
    }
  }

  return {
    pictures: pictures,
    files: data
  }
}


// List of attached files.
const FileList = ( {files, showText, showError, onClickDelete } ) => {
  // User clicked a file, navigate to it.
  async function onClickRow(file) {
    try {
      showText('Redirecting');
      let url = await storageGet(file.key);
      showText('');
      window.location.href = url.url.href;
    }
    catch (err) {
      showError(err);
    }
  }

  // Return a row per file. The cell uses justify-content-between so the
  // filename hugs the left edge while the trash button gets right-justified
  // against the right edge of the card.
  return files.map((file) => (
    <tr key={file.key} className="entered-data">
      <td>
        <div className='d-flex justify-content-between align-items-center'>
          <span onClick={() => onClickRow(file)}>
            <i className='fa fa-paperclip me-2' aria-hidden='true'></i>
            {file.name}
          </span>
          <TrashButton onClick={() => onClickDelete(file)} ariaLabel={`Delete ${file.name}`} />
        </div>
      </td>
    </tr>
  ));
}


// A list of photos.
//
// Each photo is an AttachmentImage rather than Amplify's StorageImage: these
// are full-size originals (nothing generates thumbnails for task/logbook
// attachments -- see the ~~thumbnail- filter below, which is the item's profile
// photo only), so a several-megabyte download used to sit behind a blank row
// with no indication anything was happening. AttachmentImage reserves the box
// and shows a skeleton + spinner across BOTH waits, the signed-URL fetch and
// the download itself.
//
// It deliberately reports a failed photo inline rather than through `showError`:
// one unreadable attachment should not replace the whole card's status banner,
// which is how StorageImage's onStorageGetError behaved.
const PhotoList = ({pictures, onClickDelete, showText}) => {
  async function onClickPicture(picture) {
    showText('Redirecting');
    let url = await storageGet(picture.key);
    showText('');
    window.location.href = url.url.href;
  }

  return pictures && pictures.map( (picture) => {
    // The preview if TaskPogoAttachmentPreview has written one, else the
    // original -- which is what every photo uploaded before that function
    // existed looks like, until the backfill script reaches it. This is the
    // one-line-at-the-call-site swap AttachmentImage's `imageKey` prop was
    // split out for. `sizeBytes` follows it, so the caption under the spinner
    // describes the download actually happening.
    const shown = picture.preview ?? picture;
    // A photo somebody added through a shared trip's public link, rather than one
    // the owner uploaded. Named here because this card is where they decide
    // whether to keep it, and the generated key is not a filename anyone typed.
    // An owner's own file is never guest-named, so this branch changes nothing for
    // every existing attachment.
    const fromVisitor = isGuestPhotoName(picture.name);
    const label = displayPhotoName(picture.name);
    return (
      <tr key={picture.key}>
        <td>
          <div className='d-flex justify-content-between align-items-center gap-2'>
            {/* The wrapper is what carries the tap through to the full-size
                image; AttachmentImage's own overlay sets pointer-events: none
                so the placeholder doesn't swallow it. */}
            <span className='tp-attachment-thumb-link' onClick={() => onClickPicture(picture)}>
              <AttachmentImage
                imageKey={shown.key}
                alt={label}
                sizeBytes={shown.size}
              />
              {fromVisitor && (
                <span className='badge text-bg-secondary mt-1 d-inline-block'>
                  <i className='fa-regular fa-user me-1' aria-hidden='true'></i>
                  Added by a visitor
                </span>
              )}
            </span>
            <TrashButton onClick={() => onClickDelete(picture)} ariaLabel={`Delete ${label}`} />
          </div>
        </td>
      </tr>
    );
  })
}



const Body = ({ pictures, files, onClickDelete, bucket, showText, showError, hideStatus, setPictures, setFiles }) => {
  // Byte counts for the files currently being uploaded, keyed by the final S3
  // key. StorageManager's onUploadSuccess reports only `{ key }`, but
  // processFile is handed the real File on the way in -- and that is the one
  // moment the size is knowable without re-listing the bucket.
  //
  // Worth carrying because a just-uploaded photo is the WORST case for the
  // placeholder caption: its preview does not exist yet (the S3 trigger takes a
  // couple of seconds), so the row downloads the multi-megabyte original --
  // precisely the wait the caption exists to explain. Without this it was the
  // one photo on the card with no answer to "why is this slow".
  const uploadSizes = useRef(new Map());

  function uploadError(error, file) {
    showError(error);
  }

  function uploadStarted(file) {
    // console.log(`uploadedStarted: ${JSON.stringify(file)}`);

    showText(`Uploading ${file.key}`);
  }

  // One upload landed.
  //
  // UPDATER FORM, NOT `[...pictures, file]`. Dropping four files at once
  // finishes four uploads before React re-renders, so every call read the SAME
  // `pictures`/`files` the handler had closed over -- last render's -- and each
  // one built a list of "everything that was there when the drop started, plus
  // MY file". Last writer won and three of the four photos vanished until a
  // refresh, which re-listed the bucket and showed all of them. The same rule
  // the autosave docs state: pass an updater whenever a handler can write twice
  // in one tick.
  //
  // The file goes to whichever list owns it rather than being re-split out of a
  // recombined array: `isImage` is decided by the name alone, so a file cannot
  // move between the two, and merging both lists to split them again is what
  // needed the stale copy of the other one in the first place.
  function uploadSuccess(file) {
    hideStatus();

    // Add the name property to the file.
    addNameToStorage(file, bucket);

    // A freshly uploaded photo has no preview yet -- TaskPogoAttachmentPreview
    // is an S3 trigger and has not run. Explicitly null, so it falls back to
    // the original until the next listing picks the preview up.
    const size = uploadSizes.current.get(file.key);
    uploadSizes.current.delete(file.key);

    const added = isImage(file.key)
      ? { ...file, size: size ?? file.size, preview: null }
      : file;
    const update = isImage(file.key) ? setPictures : setFiles;

    update((current) => {
      // Already listed: this was a re-upload of the same name (possibly new
      // contents), so there is nothing to add.
      if (current.some((f) => f.name === added.name)) {
        return current;
      }
      return [...current, added];
    });
  }

  // For some reason StorageManager doesn't honor the path field anymore,
  // so I added a processFile to add the bucket in (I had it in path={bucket}).
  function processFile(keyFile) {
    const key = `${bucket}${keyFile.key}`;
    // Recorded here rather than derived later: onUploadSuccess reports the key
    // and nothing else.
    if (Number.isFinite(keyFile.file?.size)) {
      uploadSizes.current.set(key, keyFile.file.size);
    }
    return {
      key,
      file: keyFile.file
    }
  }

  return (
    <div className="table-responsive">

    <table className="table table-hover text-nowrap table-striped">
      <tbody>
        <PhotoList
          pictures={pictures}
          onClickDelete={onClickDelete}
          showText={showText}
        />
        <FileList files={files} showText={showText} showError={showError} onClickDelete={onClickDelete} />
      </tbody>
    </table>

    {/* No cap on how MANY files one upload carries. `Infinity` rather than
        omitting the prop: StorageManager warns on every render when it is
        falsy, and its two internal guards then work only by accident (`n >
        undefined` is false). It is also how this codebase spells "no bound"
        elsewhere -- EntryPhotoStrip's `limit={Infinity}`. */}
    <StorageManager
      accessLevel='private'
      maxFileCount={Infinity}
      maxFileSize={MAX_UPLOAD_FILE_SIZE}
      onUploadStart={uploadStarted}
      onUploadSuccess={uploadSuccess}
      onUploadError={uploadError}
      processFile={processFile}
      isResumable
    />

  </div>
  );
}


const Attachments = ({bucket, hideLoading = false}) => {
  const isMounted = useRef(false);

  const [files, setFiles] = useState([]);
  const [pictures, setPictures] = useState([]);

  const [StatusMessage, {showText, showError, showLoading, hideStatus}] = useStatusMessage();

  // *** Initial Loading ***
  useEffect(() => {
    isMounted.current = true;

    showLoading();

    async function fetchAttachments() {
      try {
        let results = await storageList(bucket);

        if (!isMounted.current) { 
          console.log(`**Waste of time, unmounted: bucket`)
          return 
        };

        // Remove thumbnail images.
        let prefix = itemThumbnailPrefix(bucket); // `${bucket}thumbnail`;
        results = results.filter((item) => !item.key.startsWith(prefix));

        // The previews are in the same folder as the photos they belong to, so
        // one list already has both. Take them out of the list -- a preview is
        // not an attachment, and showing it would be showing every photo twice.
        const { previews, rest } = splitPreviews(results);
        results = rest;

        // Process the list to add names
        let data = processStorageList(results, bucket);

        // Update UI.
        hideStatus();

        let pictureFiles = seperatePictureFromFiles(data.files);
        setPictures(
          pictureFiles.pictures.map((picture) => ({
            ...picture,
            preview: previewFor(picture, previews),
          }))
        );
        setFiles(pictureFiles.files);
      } catch(err) {
        if (!isMounted.current) { return };
        showError(err);
      }
    }

    fetchAttachments();

    return () => {
      isMounted.current = false;
    }
  }, [bucket, hideStatus, showError, showLoading]);


  /**** Handlers ***/
  const onClickDelete = useCallback((file) => {
    showText(`Deleting ${file.name}`);

    storageDelete(file.key)
      .then(({ results }) => {
        // Best effort, and deliberately not awaited into the outcome: the
        // attachment is gone either way, and a stranded preview is invisible
        // (the listing filters previews out) and is overwritten if a photo of
        // the same name is uploaded again. Reporting a failure here would put
        // an error on a delete that succeeded.
        if (file.preview) {
          storageDelete(file.preview.key).catch((err) => {
            console.warn(`Could not delete the preview for ${file.name}:`, err);
          });
        }
        if (!isMounted.current) {
          return;
        }
        hideStatus();
        let newFiles = files.filter( (f) => { 
          return f.key !== file.key;
        });
        setFiles(newFiles);
        let newPictures = pictures.filter( (p) => {
          return p.key !== file.key;
        });
        setPictures(newPictures);
      })
      .catch((err) => {
        showError(err);
      });
  }, [files, hideStatus, pictures, showError, showText]);

  return (
    <div>
      {(!hideLoading) && StatusMessage()}
      <DataCard
        header=<CardHeader 
          text='Attached Files' 
        />
        body=<Body 
          bucket={bucket} 
          pictures={pictures} 
          files={files} 
          onClickDelete={onClickDelete} 
          showText={showText}
          showError={showError}
          hideStatus={hideStatus}
          setPictures={setPictures}
          setFiles={setFiles}
        />
      />
    </div>
  );
}

export default Attachments;